import React, { useState, MouseEvent, useRef } from 'react'
import AliceCarousel from 'react-alice-carousel'
import { color } from 'web/src/style/variables'
import { optimizeContentfulImageUrl } from 'web/src/helpers/images'
import { media } from 'web/src/style/media'

import IconMapper, { Icon } from '../icons/IconMapper'
import { useMediaPredicate } from 'react-media-hook'
import useBrands from '@/query/brands/useBrands'
import BrandCard from './BrandCard'

export const BrandsCarousel = () => {
  const [index, setIndex] = useState(0)
  const carousel = useRef<AliceCarousel>(null)

  const { brands } = useBrands()

  let isSlideChanging = false
  const isLargeDesktop = useMediaPredicate('(min-width: 1300px)')
  const maxVisibleItems = isLargeDesktop ? 4 : 3

  const responsiveImages = (brandLogo: string) => ({
    mobile: {
      imgSrc: optimizeContentfulImageUrl(brandLogo, 'fill', { width: 144, height: 144 }),
      hiResImgSrc: optimizeContentfulImageUrl(brandLogo, 'fill', { width: 144, height: 144 }),
    },
  })

  const handleDragStart = (e: MouseEvent) => e.preventDefault()

  const handleOnClick = (e: MouseEvent) => (preventDefault: boolean) => {
    if (preventDefault) {
      e.preventDefault()
    }
  }

  const handlePrevClick = () => {
    const newIndex = index - maxVisibleItems < 0 ? 0 : index - maxVisibleItems

    setIndex(newIndex)
    carousel?.current?.slideTo(newIndex)
  }

  const handleNextClick = () => {
    const newIndex = index + maxVisibleItems

    setIndex(newIndex)
    carousel?.current?.slideTo(newIndex)
  }

  if (!brands || brands.length === 0) return null

  return (
    <>
      <style jsx>{`
        .brand-carousel-wrapper {
          position: relative;
        }
        .brand-carousel-wrapper :global(.alice-carousel__wrapper) {
          padding: 0 15px !important;
        }

        .brand-carousel-wrapper :global(.alice-carousel__prev-btn),
        .brand-carousel-wrapper :global(.alice-carousel__next-btn) {
          display: none;
          cursor: pointer;
          height: 50px;
          left: 25px;
          padding: 0;
          position: absolute;
          top: 50%;
          width: 50px;
          z-index: 1;
        }
        .brand-carousel-wrapper :global(.alice-carousel__prev-btn .alice-carousel__icon-btn),
        .brand-carousel-wrapper :global(.alice-carousel__next-btn .alice-carousel__icon-btn) {
          align-items: center;
          background: ${color.brandPrimary};
          border: none;
          border-radius: 100%;
          cursor: pointer;
          display: flex;
          height: 50px;
          justify-content: center;
          padding: 0;
          width: 50px;
          box-shadow: 2px 2px 10px ${color.greyTransparent2};
        }
        .brand-carousel-wrapper :global(.alice-carousel__next-btn) {
          left: unset;
          right: 25px;
        }

        @media ${media.tabletAndHigher} {
          .brand-carousel-wrapper :global(.alice-carousel__wrapper) {
            padding: 0 44px !important;
          }
        }

        @media ${media.desktopAndHigher} {
          .brand-carousel-wrapper :global(.alice-carousel__wrapper) {
            padding: 0 60px !important;
          }
          .brand-carousel-wrapper :global(.alice-carousel__wrapper) {
            padding-left: 60px !important;
          }
          .brand-carousel-wrapper :global(.alice-carousel__prev-btn),
          .brand-carousel-wrapper :global(.alice-carousel__next-btn) {
            display: block;
          }
        }
        @media ${media.hiResDesktop} {
          .brand-carousel-wrapper {
            max-width: 1440px;
            margin: 0px auto;
          }
          .brand-carousel-wrapper :global(.alice-carousel__wrapper) {
            padding: 0 60px !important;
          }
        }
      `}</style>
      <div className="brand-carousel-wrapper">
        <AliceCarousel
          autoWidth
          disableDotsControls
          disableButtonsControls
          mouseTracking
          onSlideChange={() => (isSlideChanging = true)}
          onSlideChanged={(e) => {
            isSlideChanging = false
            setIndex(e.item)
          }}
          ref={carousel}
          items={brands.map(({ slug, brandLogo, brandName }) => (
            <BrandCard
              key={slug}
              {...{
                brandLogo,
                brandName,
                slug,
                handleDragStart,
                handleOnClick,
                responsiveImages,
                isSlideChanging,
              }}
            />
          ))}
        />
        {index > 0 && (
          <div className="alice-carousel__prev-btn">
            <button type="button" aria-label="Previous" className="alice-carousel__icon-btn" onClick={handlePrevClick}>
              <IconMapper icon={Icon.ArrowLeft} color={color.white} size={20} />
            </button>
          </div>
        )}
        {index < brands.length - maxVisibleItems && (
          <div className="alice-carousel__next-btn">
            <button type="button" aria-label="Next" className="alice-carousel__icon-btn" onClick={handleNextClick}>
              <IconMapper icon={Icon.ArrowRight} color={color.white} size={20} />
            </button>
          </div>
        )}
      </div>
    </>
  )
}
